import { Entity } from "../../Entity";
import PersonFactory from "../../Factories/PersonFactory";
import { FactoryInterface } from "../../FactoryInterface";
import Image from "../../../Image/Image";
import { PersonInterface } from "./PersonInterface";
import { CompanyInterface } from "../Company/CompanyInterface";
import { RoleInterface } from "./Role/RoleInterface";
import { AddressInterface } from "../../../Interface/AddressInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import { ContractInterface } from "../Contract/ContractInterface";
import { SubscriptionInterface } from "../Subscription/SubscriptionInterface";
import { Admin } from "./Role/Roles/Admin";
import { User } from "./Role/Roles/User";
import { Owner } from "./Role/Roles/Owner";
import { Vendor } from "./Role/Roles/Vendor";
import { None } from "./Role/Roles/None";
import { Occupant } from "./Role/Roles/Occupant";
import Translator from "../../../Translation/Translator";
import SubscriptionTypeContainer from "../../../Entity/Entities/Subscription/SubscriptionType/SubscriptionTypeContainer";
import { UserSettingsInterface } from "../Settings/User/UserSettingsInterface";
import Store from "../../../Store/Store";

class Person extends Entity implements PersonInterface {

  constructor(public data) {
    super();
  }

  get nameOfEntity() {
    return Translator.trans("person");
  }

  get nameForUrl() {
    return "person";
  }

  get labelForEntity() {
    return "label-person"
  }

  public getFactory(): FactoryInterface {
    return PersonFactory;
  }

  get id(): number {
    return this.data.id;
  }

  get amazonFolderName(): string {
    return this.data.amazonFolderName;
  }

  get firstName(): string {
    if (!this.data.firstName) {
      return "";
    }

    return this.data.firstName;
  }

  get lastName(): string {
    if (!this.data.lastName) {
      return "";
    }

    return this.data.lastName;
  }

  get displayName(): string {
    const displayName = `${this.data.firstName} ${this.data.lastName}`
    return displayName?.trim() || "";
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get email(): string {
    return this.data.email;
  }

  get newEmail(): string {
    return this.data.newEmail;
  }

  get contactStatusId(): number {
    return this.data.contactStatusId;
  }

  get agencyId(): number {
    return this.data.agencyId;
  }

  get role(): RoleInterface {
    return this.data.role;
  }

  get avatar(): string {
    return this.data.avatar;
  }

  get phone1(): string {
    return this.data.phone1;
  }

  get phone2(): string {
    return this.data.phone2;
  }

  get profession(): string {
    return this.data.profession;
  }

  get companyId(): number {
    return this.data.companyId;
  }

  get company(): CompanyInterface | undefined {
    return this.relation("app.companies", this.data.companyId)[0];
  }

  get companies(): CompanyInterface[] {
    return this.data.companies;
  }

  get address(): AddressInterface {
    return this.data.address;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get updatedAt(): Date {
    return this.data.updatedAt;
  }

  get createdAt(): Date {
    return this.data.createdAt;
  }

  get mainBankAccountId(): string {
    return this.data.mainBankAccountId;
  }

  get mainBankAccountName(): string {
    return this.data.mainBankAccountName;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  get dateOfBirth(): Date {
    return this.data.dateOfBirth;
  }

  get placeOfBirthAddress(): string {
    return this.data.placeOfBirthAddress;
  }

  get identityNumber(): string {
    return this.data.identityNumber;
  }

  get identityType(): object {
    return this.data.identityType;
  }

  get documentAddress(): any {
    return this.data.documentAddress;
  }

  get createdBySystem(): boolean {
    return this.data.createdBySystem;
  }

  get agency(): CompanyInterface {
    const agency: CompanyInterface = Store.getConfigurator().getState()?.app?.companies?.find((company: CompanyInterface) => company?.isAgency);

    return agency;
  }

  get contracts(): ContractInterface[] {
    return (this.contactForContracts || []).map(contact => {
      return Store.getConfigurator().getState()?.app?.contracts?.find((contract: ContractInterface) => contract?.id === contact?.contractId);
    })
  }

  get subscription(): SubscriptionInterface {
    return this.subscriptionRelation(SubscriptionTypeContainer.pickIdFromLabel("Contacts"), this.data.id);
  }

  get isSubscribed(): boolean {
    return this.subscription !== null;
  }

  public roleDisplayName(): string {
    if (this.isAdmin() || this.isUser()) {
      return Translator.trans("label-user");
    }

    if (this.contracts.length > 0) {
      return Translator.trans("label-tenant");
    }

    return Translator.trans("label-person");
  }

  public isAdmin(): boolean {
    return this.role && this.role.getRoleId() === Admin.contactRoleId;
  }

  public isUser(): boolean {
    return this.role && this.role.getRoleId() === User.contactRoleId;
  }

  public isTenant(): boolean {
    return this.contracts.length > 0;
  }

  public isOwner(): boolean {
    return this.role && this.role.getRoleId() === Owner.contactRoleId;
  }

  public isVendor(): boolean {
    return this.role && this.role.getRoleId() === Vendor.contactRoleId;
  }

  public isNone(): boolean {
    return this.role && this.role.getRoleId() === None.contactRoleId;
  }

  public isOccupant(): boolean {
    return this.role && this.role.getRoleId() === Occupant.contactRoleId;
  }

  public renderAvatar(params: any = {}): any {
    return Image.getAvatarAbsolutely(this.data.avatar, "person", params);
  }

  get note(): string {
    return this.data.note;
  }

  get genderTypeId(): number {
    return this.data.genderTypeId;
  }

  get languageTypeId(): number {
    return this.data.languageTypeId;
  }

  get userSettings(): UserSettingsInterface {
    return this.data.userSettings;
  }

  public getConfirmationStatus(): string {
    if (this.contactStatusId === 1) {
      return (Translator.trans("status-confirmed")).toLowerCase();
    }

    return (Translator.trans("status-not-confirmed")).toLowerCase();
  }

  public getConfirmationStatusColor(): string {
    if (this.contactStatusId === 1) {
      return "#43A047";
    }

    return "#F44336";
  }

  get contactForContracts(): any[] {
    return this.data.contactForContracts;
  }

  get isAccountConfirmed(): boolean {
    return this.contactStatusId === 1; // todo fix hardcoded value
  }

  get isReadonly(): boolean {
    return this.removed;
  }
}

export { Person }
